import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import PageMeta from "../components/PageMeta";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  PM_COLORS,
  publicCardSx,
  publicHeroChipSx,
  publicSubtitleSx,
  publicTitleSx,
} from "../constants/publicMarketingTheme";
import { resolveUploadUrl } from "../utils/resolveUploadUrl";

function formatPostDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(
          `/api/public/blog-posts/${encodeURIComponent(slug)}`,
        );
        if (!cancelled) setPost(data.post || null);
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.data?.error ||
              e.message ||
              "Could not load this article.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const metaDescription =
    post?.excerpt?.trim() ||
    (post?.body ? String(post.body).trim().slice(0, 160) : "");

  const coverSrc = post?.cover_image_url
    ? resolveUploadUrl(post.cover_image_url)
    : "";

  return (
    <PublicMarketingShell>
      {post ? (
        <PageMeta
          title={`${post.title} — JD Integrations`}
          description={metaDescription}
          path={`/blog/${post.slug}`}
        />
      ) : null}
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          color="inherit"
          onClick={() => navigate("/blog")}
          sx={{ mb: 2, color: publicSubtitleSx.color }}
        >
          Back to blog
        </Button>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : error ? (
          <Typography color="error.light">{error}</Typography>
        ) : post ? (
          <Paper
            sx={{
              ...publicCardSx,
              overflow: "hidden",
              p: 0,
            }}
          >
            {coverSrc ? (
              <Box
                component="img"
                src={coverSrc}
                alt=""
                sx={{
                  width: "100%",
                  maxHeight: 420,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : null}
            <Stack spacing={2} sx={{ p: { xs: 2.5, md: 4 } }}>
              <Chip label="Article" size="small" sx={publicHeroChipSx} />
              <Typography variant="h3" sx={publicTitleSx}>
                {post.title}
              </Typography>
              <Typography variant="body2" sx={{ color: PM_COLORS.textMuted }}>
                {formatPostDate(post.published_at || post.created_at)}
              </Typography>
              {post.excerpt ? (
                <Typography
                  variant="h6"
                  sx={{ ...publicSubtitleSx, fontWeight: 500 }}
                >
                  {post.excerpt}
                </Typography>
              ) : null}
              <Typography
                component="div"
                sx={{
                  ...publicSubtitleSx,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.75,
                }}
              >
                {post.body}
              </Typography>
            </Stack>
          </Paper>
        ) : null}
      </Container>
    </PublicMarketingShell>
  );
}
